/*
 * Designer Rugs — motion orchestrator.

 * Ground rules enforced here:
 * - Progressive enhancement: no CSS opacity:0 anywhere — initial states
 * are gsap.setbehind the js-motion-ready guard; JS off/failed means
 * everything stays visible. Reveals only translate/fade (zero CLS).
 * - prefers-reduced-motion is a HARD override: nothing initialises, and
 * a live change mid-session kills every effect.
 * - LCP protection: hero/first-viewport media never animates; text that
 * is in the first viewport animates on load, not on scroll.
 * - Theme editor: init/teardown re-runs on shopify:section:load/unload
 * (ScrollTriggers killed + rebuilt, SplitTexts reverted).
 * - Analytics untouched: no click interception — view-transition naming
 * decorates the element and lets the navigation proceed normally.
 */
if (!window.DRMotion) {
  window.DRMotion = (() => {
    // Missing config = defaults on (theme.liquid only loads this file
    // when the master setting is on). Read lazily at boot so script
    // order can never race the config element.
    const config = { smoothScroll: true, headingReveals: true, scrollFades: true, pageTransitions: true };
    function readConfig() {
      const configEl = document.querySelector('[data-motion-config]');
      if (configEl) {
        try {
          Object.assign(config, JSON.parse(configEl.textContent));
        } catch (e) { /* Malformed config falls back to defaults*/ }
      }
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const state = { lenis: null, triggers: new Set(), splits: new Set(), enabled: false };

    const hasGsap = typeof gsap !== 'undefined';
    if (hasGsap && typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);
    if (hasGsap && typeof SplitText !== 'undefined') gsap.registerPlugin(SplitText);

    /* ---------------- reveals (GSAP + ScrollTrigger) ----------------*/
    // Subtle editorial: fade-up 20px, 0.95s expo.out, 90ms stagger, once.
    // Trigger line at 78% of the viewport (client: higher + slower).
    // 21.4: .featured__card/.featured__item moved OUT of the generic reveal —
    // the featured carousel has its own left-to-right staggered entrance
    // (initFeaturedEntrance). The base.css js-motion-intro guard still lists
    // them (they must stay hidden pre-paint for the dedicated intro too).
    const REVEAL_SELECTOR = [
      '.card-product',
      '.journal-card',
      '.tranches__card',
      '.concierge__tile',
      '.site-footer__col',
      '.pdp-acc',
      '.pdp-blog-card',
      '.hero__copy',
      '.tranches__heading',
      '.journal__heading',
      '.ymal__head',
      '.pdp-coll__rail',
      '.pdp-blog__rail',
      '.media-split__panel',
      '.journal-tile',
      /* THE GENERIC HOOK. Coverage used to be an accident of
         which class names a section happened to use, so 32 of 60 sections and
         16 templates animated nothing. A section now opts in by stamping
         [data-reveal] on its repeating CHILD wrappers (cards, panels,
         figures, copy blocks) and [data-reveal-heading] on its display-serif
         heading. 🔴 NEVER stamp either on .shopify-section, on <main>, or on
         a section root that wraps a sticky bar or a fixed dialog: the tween
         animates `transform`, and a transformed ancestor becomes the
         containing block for position:sticky/fixed descendants. Stamp
         the children, never the wrapper that holds the sticky thing.*/
      '[data-reveal]',
    ].join(', ');
    // Never animate: chrome, drawers/menus/lightboxes, hero media.
    const EXEMPT_CLOSEST = [
      '.site-header',
      '.announcement-bar',
      '.cart-drawer',
      '.side-sheet',
      '.nav-drawer',
      '.mega',
      '.video-lightbox',
      '.pdp-lightbox',
      /* The two remaining position:fixed dialogs. They were
         missing because nothing inside them ever matched a reveal class —
         with [data-reveal] available they could, so they are named now.*/
      '.browse-by',
      '.explorug-lightbox',
      '.hero__media',
    ].join(', ');

    const multiplier = () =>
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--motion-multiplier')) || 1;

    function collectRevealTargets(root) {
      const scope = root.matches?.(REVEAL_SELECTOR) ? [root] : [];
      return scope.concat(Array.from(root.querySelectorAll(REVEAL_SELECTOR))).filter((el) => {
        if (el.dataset.motionDone) return false;
        if (el.closest(EXEMPT_CLOSEST)) return false;
        // ONE treatment per element ( de-glitch): display headings
        // (.title-1 on journal/tranches headings) matched both lists and
        // fade-up + line-rise ran together — the SplitText owns them.
        if (el.matches(HEADING_SELECTOR)) return false;
        el.dataset.motionDone = 'true';
        return true;
      });
    }

    function initReveals(root) {
      if (!config.scrollFades || !hasGsap || typeof ScrollTrigger === 'undefined') return;
      const targets = collectRevealTargets(root);
      if (!targets.length) return;
      const m = multiplier();
      const inView = [];
      const below = [];
      targets.forEach((el) => {
        const rect = el.getBoundingClientRect();
        (rect.top < window.innerHeight ? inView : below).push(el);
      });
      // First-viewport elements: on-load variant (no scroll trigger).
      // cap the TOTAL first-paint stagger — 0.09s × a 10-card
      // grid row left the rightmost cards invisible for ~1s after their
      // images had already loaded (the reveal, not the request, was the
      // stagger QA saw). Small sets keep the editorial 90ms feel.
      // 215 J9 — a card INSIDE a horizontal rail reveals in place, never on a
      // rise. Parking rail cards at y:20 made every rail 20px taller than its
      // box until the last tween cleared (measured in WebKit: journal 452
      // against a 432 box, PDP band 2 431/411, YMAL 565/545), and because
      // overflow-x:auto makes overflow-y compute to auto, the rail was a
      // vertical scroll container for exactly that window — a drifted left
      // swipe moved it up and down. Fade only inside a rail; the rise stays
      // everywhere else.
      const inRail = (el) => !!el.closest('[data-scroller]');
      const rise = (els, y) => { const a = els.filter((el) => !inRail(el)); const b = els.filter(inRail); if (a.length) gsap.set(a, { autoAlpha: 0, y }); if (b.length) gsap.set(b, { autoAlpha: 0 }); };
      if (inView.length) {
        rise(inView, 16);
        const each = Math.min(0.09, 0.45 / inView.length);
        gsap.to(inView, { autoAlpha: 1, y: 0, duration: 0.95 * m, ease: 'expo.out', stagger: each, clearProps: 'all' });
      }
      if (below.length) {
        rise(below, 20);
        const batch = ScrollTrigger.batch(below, {
          start: 'top 78%',
          once: true,
          onEnter: (els) =>
            gsap.to(els, { autoAlpha: 1, y: 0, duration: 0.95 * m, ease: 'expo.out', stagger: 0.09, clearProps: 'all' }),
        });
        batch.forEach((t) => state.triggers.add(t));
        rescueUnreachable();
      }
    }

    // With the trigger line at 78%, elements near the very end of the
    // page may sit below the line even at max scroll — they would never
    // reveal. Fire any trigger that can never be reached.
    function rescueUnreachable() {
      if (typeof ScrollTrigger === 'undefined') return;
      requestAnimationFrame(() => {
        const max = ScrollTrigger.maxScroll(window);
        state.triggers.forEach((trigger) => {
          if (trigger.start > max && !trigger.isActive) {
            const el = trigger.trigger;
            trigger.kill();
            state.triggers.delete(trigger);
            if (el) gsap.to(el, { autoAlpha: 1, y: 0, yPercent: 0, duration: 0.95 * multiplier(), ease: 'expo.out', clearProps: 'all' });
            if (trigger.vars?.onEnter && !el) trigger.vars.onEnter();
          }
        });
      });
    }

    // Start positions move when images size in — re-check reachability on
    // every refresh so late-page triggers can neither die unreached nor
    // fire twice (kill + Set removal make the rescue one-shot per trigger).
    if (hasGsap && typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.addEventListener('refresh', () => {
        if (state.enabled) rescueUnreachable();
      });
    }

    /* ---------------- featured carousel entrance (21.4) ----------------*/
    // Left-to-right stagger, once, when the section enters the viewport.
    // DOM order IS visual order on a horizontal rail, so a plain stagger
    // reads L-R. Re-init safe: keyed by data-motion-featured; triggers are
    // tracked in state.triggers so editor teardown/rebuild works.
    function initFeaturedEntrance(root) {
      if (!config.scrollFades || !hasGsap || typeof ScrollTrigger === 'undefined') return;
      const rails = root.matches?.('.featured') ? [root] : Array.from(root.querySelectorAll('.featured'));
      rails.forEach((rail) => {
        if (rail.dataset.motionFeatured) return;
        rail.dataset.motionFeatured = 'true';
        const cards = Array.from(rail.querySelectorAll('.featured__card, .featured__item'));
        if (!cards.length) return;
        const m = multiplier();
        gsap.set(cards, { autoAlpha: 0, x: 32 });
        const trigger = ScrollTrigger.create({
          trigger: rail,
          start: 'top 85%',
          once: true,
          onEnter: () =>
            gsap.to(cards, { autoAlpha: 1, x: 0, duration: 0.9 * m, ease: 'expo.out', stagger: 0.08, clearProps: 'all' }),
        });
        state.triggers.add(trigger);
        // a rail already in view at boot fires immediately via ScrollTrigger;
        // a rail below the fold that can never be reached is rescued
        rescueUnreachable();
      });
    }

    /* ---------------- heading reveals (SplitText) ----------------*/
    // Display serif voice only, split by LINES with a masked rise — never
    // per-character. Splits revert on completion (clean DOM for a11y/SEO).
    /* [data-reveal-heading] is the generic hook — see the note on the reveal
       list above. Masked line-rise, display-serif voice only.*/
    const HEADING_SELECTOR = ['.title-1', '.title-2', '.hero__heading', '.concierge__heading', '.ymal__heading', '.pdp-blog__heading', '[data-reveal-heading]'].join(', ');

    function initHeadingReveals(root) {
      if (!config.headingReveals || !hasGsap || typeof SplitText === 'undefined') return;
      const scope = root.matches?.(HEADING_SELECTOR) ? [root] : [];
      const headings = scope.concat(Array.from(root.querySelectorAll(HEADING_SELECTOR))).filter((el) => {
        if (el.dataset.motionSplit) return false;
        if (el.closest('[data-slide]') && !el.closest('[data-slide]').classList.contains('is-active')) return false;
        if (el.closest(EXEMPT_CLOSEST)) return false;
        // ONE treatment per element ( de-glitch): a heading inside a
        // fade-up container (hero copy, YMAL head) is carried by the
        // container — a line rise inside a still-transparent parent read
        // as the intro playing twice.
        if (el.parentElement?.closest(REVEAL_SELECTOR)) return false;
        el.dataset.motionSplit = 'true';
        return true;
      });
      if (!headings.length) return;
      const m = multiplier();
      const splitAndPlay = (el) => {
        if (el.closest('[data-slide]') && !el.closest('[data-slide]').classList.contains('is-active')) {
          delete el.dataset.motionSplit;
          return;
        }
        const inView = el.getBoundingClientRect().top < window.innerHeight;
        const split = SplitText.create(el, { type: 'lines', mask: 'lines', linesClass: 'motion-line' });
        // Keep our descender allowance independent of GSAP's generated mask name.
        split.lines.forEach((line) => line.parentElement.classList.add('motion-line-mask'));
        // the resize handler has to know WHICH element a pending split owns so
        // it can clear the marker and let it be rebuilt; SplitText exposes
        // `.elements`, but carrying it explicitly survives a version change.
        split.__drEl = el;
        state.splits.add(split);
        const play = () => {
          gsap.fromTo(
            split.lines,
            { yPercent: 0, y: (_, line) => line.parentElement.getBoundingClientRect().height + 2 },
            {
              yPercent: 0,
              y: 0,
              duration: 0.85 * m,
              ease: 'power3.out',
              // Read as successive lines without delaying a long heading excessively.
              stagger: { each: Math.min(0.16, 0.64 / Math.max(1, split.lines.length - 1)), from: 'start' },
              onComplete: () => {
                split.revert();
                state.splits.delete(split);
              },
            }
          );
        };
        if (inView) {
          // first-viewport text animates on load — never on scroll
          play();
        } else {
          gsap.set(split.lines, { yPercent: 0, y: (_, line) => line.parentElement.getBoundingClientRect().height + 2 });
          const trigger = ScrollTrigger.create({ trigger: el, start: 'top 78%', once: true, onEnter: play });
          state.triggers.add(trigger);
          requestAnimationFrame(() => {
            if (state.triggers.has(trigger) && trigger.start > ScrollTrigger.maxScroll(window)) {
              trigger.kill();
              state.triggers.delete(trigger);
              play();
            }
          });
        }
      };
      // Split only once the brand faces are live: splitting against the
      // fallback font re-wraps the lines when the woff2 lands — a visible
      // mid-animation glitch. Fonts are preloaded, so the pending path is
      // rare and brief; headings hold at autoAlpha 0 meanwhile.
      // 17.1: splitting forces a synchronous layout per heading. On a cold
      // cache the fonts land right around the visitor's FIRST scroll, and that
      // reflow mid-scroll was part of the first-scroll hitch — so once fonts
      // arrive, wait for the scroll to go quiet (180ms) before splitting,
      // capped at 1.2s so headings never hold invisible for long.
      const whenScrollQuiet = (fn) => {
        let timer = null;
        let done = false;
        const run = () => {
          if (done) return;
          done = true;
          window.removeEventListener('wheel', bump);
          window.removeEventListener('scroll', bump);
          fn();
        };
        const bump = () => {
          clearTimeout(timer);
          timer = setTimeout(run, 180);
        };
        window.addEventListener('wheel', bump, { passive: true });
        window.addEventListener('scroll', bump, { passive: true });
        bump();
        setTimeout(run, 1200);
      };
      if (!document.fonts || document.fonts.status === 'loaded') {
        headings.forEach(splitAndPlay);
      } else {
        gsap.set(headings, { autoAlpha: 0 });
        document.fonts.ready.then(() => {
          whenScrollQuiet(() => {
            headings.forEach((el) => {
              if (!state.enabled || !el.isConnected) return;
              gsap.set(el, { clearProps: 'visibility,opacity' });
              splitAndPlay(el);
            });
          });
        });
      }
    }

    // Hidden slides must not consume their reveal at boot. Animate the heading
    // on activation, and cleanly cancel the previous split on rapid navigation.
    document.addEventListener('dr:hero-slide-change', (event) => {
      const hero = event.target;
      if (!state.enabled || reduced.matches || !hero.matches?.('hero-slideshow')) return;
      state.splits.forEach((split) => {
        if (!hero.contains(split.__drEl)) return;
        gsap.killTweensOf(split.lines);
        split.revert();
        state.splits.delete(split);
      });
      hero.querySelectorAll('.hero__heading').forEach((heading) => {
        delete heading.dataset.motionSplit;
        gsap.set(heading, { clearProps: 'visibility,opacity' });
      });
      initHeadingReveals(hero);
    });

    /* ---------------- Lenis smooth scroll ----------------*/
    function initLenis() {
      if (!config.smoothScroll || typeof Lenis === 'undefined') return;
      // Desktop fine pointers only; touch keeps native scrolling.
      if (!window.matchMedia('(pointer: fine)').matches) return;
      // Initialise at DOM readiness, not after images/fonts or a 3.5s timer.
      // If native scrolling has already started, keep it for this page rather
      // than changing the wheel behaviour in the middle of a gesture.
      if (window.__drNativeScrollStarted || window.scrollY > 0) return;
      if (!state.enabled || state.lenis || reduced.matches) return;
      state.lenis = new Lenis({ autoRaf: true, smoothWheel: true, syncTouch: false });
      if (typeof ScrollTrigger !== 'undefined') {
        state.lenis.on('scroll', ScrollTrigger.update);
      }
      if (document.documentElement.style.overflow === 'hidden') state.lenis.stop();
      // Scroll-lock coordination: every drawer/sheet/lightbox sets
      // documentElement.style.overflow — observe it rather than patching
      // each component. (Registered before takeover; guards on state.lenis.)
      const lockObserver = new MutationObserver(() => {
        if (!state.lenis) return;
        if (document.documentElement.style.overflow === 'hidden') state.lenis.stop();
        else state.lenis.start();
      });
      lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

      // Anchor links (concierge -> showroom anchors) ride Lenis.
      document.addEventListener('click', (event) => {
        if (!state.lenis) return;
        /* 🔴 DEFER TO A MORE SPECIFIC HANDLER. This document-level
           handler owns generic same-page anchors (concierge -> showroom), and
           its offset is `chrome + 16` with NO term for a sticky in-page bar.
           On the three guide pages that made it the real cause of the client's
           "anchor lands mid-section": the guide bar's own click handler
           computed the correct destination, this one then fired on the bubble
           and re-scrolled to a formula that does not know the bar exists, and
           Lenis animated LAST so it won. The CSS scroll-margin was never
           reached at all on those pages. The bar now stops propagation, and
           this guard is the second line of defence.*/
        if (event.defaultPrevented) return;
        const link = event.target.closest('a[href*="#"]');
        if (!link || link.closest('[role="dialog"]')) return;
        let url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (e) {
          return;
        }
        if (url.pathname !== window.location.pathname || url.hash.length < 2) return;
        let target = null;
        try {
          target = document.querySelector(url.hash);
        } catch (e) {
          return;
        }
        if (!target) return;
        event.preventDefault();
        const chrome = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--chrome-height')) || 0;
        state.lenis.scrollTo(target, { offset: -(chrome + 16) });
        history.pushState({}, '', url.hash);
      });
    }

    /* ---------------- view-transition shared element ----------------*/
    function initTransitionNaming() {
      if (!config.pageTransitions) return;
      // Name the clicked card image so it morphs into the PDP gallery
      // hero. One named element per transition; the navigation itself is
      // untouched (real page load — analytics see it normally).
      document.addEventListener('click', (event) => {
        const card = event.target.closest('.card-product');
        if (!card || !event.target.closest('a')) return;
        document.querySelectorAll('[style*="view-transition-name"]').forEach((el) => {
          el.style.viewTransitionName = '';
        });
        const img = card.querySelector('.card-product__img');
        if (img) img.style.viewTransitionName = 'product-hero';
      });
      // bfcache restore: clear stray names so the next transition is clean.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          document.querySelectorAll('[style*="view-transition-name"]').forEach((el) => {
            el.style.viewTransitionName = '';
          });
        }
      });
    }

    /* ---------------- teardown / editor lifecycle ----------------*/
    function teardownWithin(root) {
      root.querySelectorAll?.('[data-motion-featured]').forEach((el) => el.removeAttribute('data-motion-featured'));
      if (root.matches?.('[data-motion-featured]')) root.removeAttribute('data-motion-featured');
      state.triggers.forEach((trigger) => {
        const el = trigger.trigger || trigger.vars?.trigger;
        if (el && root.contains(el)) {
          trigger.kill();
          state.triggers.delete(trigger);
        }
      });
      state.splits.forEach((split) => {
        const owner = split.elements?.[0];
        if (owner && root.contains(owner)) {
          split.revert();
          state.splits.delete(split);
        }
      });
    }

    function killAll() {
      state.triggers.forEach((t) => t.kill());
      state.triggers.clear();
      state.splits.forEach((split) => split.revert());
      state.splits.clear();
      if (hasGsap) gsap.globalTimeline.clear();
      document.querySelectorAll('[data-motion-done], [data-motion-split], [data-motion-featured]').forEach((el) => {
        el.removeAttribute('data-motion-done');
        el.removeAttribute('data-motion-split');
        el.removeAttribute('data-motion-featured');
        if (hasGsap) gsap.set(el, { clearProps: 'all' });
      });
      if (state.lenis) {
        state.lenis.destroy();
        state.lenis = null;
      }
      document.documentElement.classList.remove('js-motion-ready');
      document.documentElement.classList.remove('js-motion-intro');
      state.enabled = false;
    }

    function initAll(root) {
      initReveals(root);
      initFeaturedEntrance(root);
      initHeadingReveals(root);
    }

    /* ---------------- boot ----------------*/
    function boot() {
      if (reduced.matches || state.enabled) {
        // never leave the pre-paint guard behind (reduced-motion path)
        document.documentElement.classList.remove('js-motion-intro');
        return;
      }
      readConfig();
      state.enabled = true;
      document.documentElement.classList.add('js-motion-ready');
      initLenis();
      initTransitionNaming();
      initAll(document);
      // Initial states are inline now — lift the pre-paint intro guard
      // (theme.liquid stamps it before first paint so nothing flashes
      // visible and then replays: the intro fires exactly once).
      document.documentElement.classList.remove('js-motion-intro');
    }

    // live reduced-motion change kills everything immediately
    reduced.addEventListener?.('change', () => {
      if (reduced.matches) killAll();
      else boot();
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (!state.enabled) return;
      teardownWithin(event.target);
      initAll(event.target);
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (!state.enabled) return;
      teardownWithin(event.target);
    });

    /* SplitText masks must not survive a resize mid-animation.
     *
     * 🔴 REVERTING WAS ONLY HALF OF IT, AND THE MISSING HALF WAS THE DEFECT
     * (p203, 01.09). This handler reverted every pending split and cleared the
     * set — but `dataset.motionSplit` stayed 'true', so `initHeadingReveals`
     * filtered those headings out for the life of the page and nothing ever
     * re-split them. A single resize therefore PERMANENTLY skipped the
     * line-rise of every heading that had not yet played.
     *
     * ⚠ THAT IS WHY IT PRESENTED AS "GLITCHY IN SAFARI" RATHER THAN AS A BUG:
     * the heading still ends fully visible, so nothing looks broken — some
     * headings simply animate and others do not, apparently at random. And
     * Safari fires viewport resizes as its URL bar collapses and expands while
     * you scroll, so it lands there far more often than in Chrome.
     * Reproduced before the fix: a below-fold heading showed 1 line wrapper
     * with no resize and 0 after one, `motionSplit` stuck at 'true' both times.
     *
     * ⚠ ONLY PENDING SPLITS ARE REBUILT, AND THAT IS LOAD-BEARING. A split
     * that has PLAYED reverts and deletes itself in its own onComplete, so it
     * is already out of `state.splits` — re-splitting those would replay
     * finished headings on every resize, which is a worse glitch than the one
     * being fixed. The set is exactly the right population; it just was not
     * being finished off.
     */
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const pending = [];
        state.splits.forEach((split) => {
          const owner = split.__drEl || (split.elements && split.elements[0]);
          if (owner && owner.isConnected) pending.push(owner);
          split.revert();
        });
        state.splits.clear();
        if (!pending.length || !state.enabled) return;
        // clear the marker and kill the now-stale trigger, or the rebuild is
        // filtered straight back out and the trigger fires against detached
        // line elements.
        pending.forEach((el) => { delete el.dataset.motionSplit; });
        state.triggers.forEach((t) => {
          if (pending.indexOf(t.trigger) !== -1) {
            t.kill();
            state.triggers.delete(t);
          }
        });
        initHeadingReveals(document);
      }, 150);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
      boot();
    }

    return {
      get lenis() {
        return state.lenis;
      },
      get enabled() {
        return state.enabled;
      },
      initAll,
      killAll,
    };
  })();
}
